import { useState, useRef, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PenLine, Save, ImagePlus, Loader2, CheckCircle2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/contexts/AuthContext";

export function SignatureEditor() {
  const [html, setHtml] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [lastSaved, setLastSaved] = useState(null);
  const editorRef = useRef(null);
  const imageInputRef = useRef(null);
  const isInitializedRef = useRef(false);

  useEffect(() => {
    loadSignature();
  }, []);

  const loadSignature = async () => {
    try {
      const res = await api.get("/signature");
      if (res.data.html) {
        setHtml(res.data.html);
        if (editorRef.current) {
          editorRef.current.innerHTML = res.data.html;
        }
        setLastSaved(res.data.updated_at);
      }
    } catch {
      // no signature yet
    }
    setLoading(false);
  };

  useEffect(() => {
    if (editorRef.current && html && !isInitializedRef.current) {
      editorRef.current.innerHTML = html;
      isInitializedRef.current = true;
    }
  }, [html]);

  const handleInput = useCallback(() => {
    if (editorRef.current) {
      setHtml(editorRef.current.innerHTML);
    }
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.post("/signature", { html });
      setLastSaved(res.data.updated_at);
      toast.success("Signature sauvegardée");
    } catch {
      toast.error("Erreur de sauvegarde");
    }
    setSaving(false);
  };

  const handleClear = () => {
    setHtml("");
    if (editorRef.current) {
      editorRef.current.innerHTML = "";
    }
  };

  const handleImageUpload = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Seules les images sont acceptées");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Image trop volumineuse (max 2 Mo)");
      return;
    }

    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target.result;
      const img = new Image();
      img.onload = () => {
        let w = img.naturalWidth;
        let h = img.naturalHeight;
        const maxW = 180;
        if (w > maxW) {
          h = Math.round((h * maxW) / w);
          w = maxW;
        }
        if (editorRef.current) {
          editorRef.current.focus();
          document.execCommand(
            "insertHTML",
            false,
            `<img src="${dataUrl}" width="${w}" height="${h}" style="width:${w}px;height:${h}px;max-width:${w}px;" />`
          );
          setHtml(editorRef.current.innerHTML);
        }
      };
      img.src = dataUrl;
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  }, []);

  const handlePaste = useCallback(async (e) => {
    const clipboardData = e.clipboardData;
    if (!clipboardData) return;

    const files = Array.from(clipboardData.files || []);
    const imageFile = files.find((f) => f.type.startsWith("image/"));
    if (imageFile) {
      e.preventDefault();
      const reader = new FileReader();
      reader.onload = (ev) => {
        const dataUrl = ev.target.result;
        document.execCommand(
          "insertHTML",
          false,
          `<img src="${dataUrl}" style="max-width:200px;height:auto;" />`
        );
        if (editorRef.current) setHtml(editorRef.current.innerHTML);
      };
      reader.readAsDataURL(imageFile);
      return;
    }

    const pastedHtml = clipboardData.getData("text/html");
    if (pastedHtml && /<img\b/i.test(pastedHtml)) {
      e.preventDefault();
      const temp = document.createElement("div");
      temp.innerHTML = pastedHtml;

      let hasFailedImages = false;
      const images = temp.querySelectorAll("img");
      for (const img of images) {
        const src = img.getAttribute("src") || "";
        if (src.startsWith("data:")) continue;

        if (src.startsWith("http://") || src.startsWith("https://")) {
          let converted = false;
          try {
            const resp = await fetch(src, { mode: "cors" });
            if (resp.ok) {
              const blob = await resp.blob();
              const dataUrl = await new Promise((resolve) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result);
                reader.onerror = () => resolve(null);
                reader.readAsDataURL(blob);
              });
              if (dataUrl) {
                img.setAttribute("src", dataUrl);
                converted = true;
              }
            }
          } catch {
            // CORS blocked - try canvas approach
          }

          if (!converted) {
            try {
              const imgEl = new Image();
              imgEl.crossOrigin = "anonymous";
              const loaded = await new Promise((resolve) => {
                imgEl.onload = () => resolve(true);
                imgEl.onerror = () => resolve(false);
                imgEl.src = src;
              });
              if (loaded && imgEl.naturalWidth > 0) {
                const canvas = document.createElement("canvas");
                canvas.width = imgEl.naturalWidth;
                canvas.height = imgEl.naturalHeight;
                canvas.getContext("2d").drawImage(imgEl, 0, 0);
                const dataUrl = canvas.toDataURL("image/png");
                if (dataUrl && dataUrl.length > 100) {
                  img.setAttribute("src", dataUrl);
                  converted = true;
                }
              }
            } catch {
              // tainted canvas
            }
          }

          if (!converted) {
            const placeholder = document.createElement("span");
            placeholder.textContent = "[IMAGE]";
            placeholder.style.cssText = "display:inline-block;padding:4px 8px;background:#fef2f2;border:1px dashed #fca5a5;border-radius:4px;color:#dc2626;font-size:11px;";
            img.replaceWith(placeholder);
            hasFailedImages = true;
          }
        }
      }

      document.execCommand("insertHTML", false, temp.innerHTML);
      if (editorRef.current) setHtml(editorRef.current.innerHTML);

      if (hasFailedImages) {
        toast.error(
          "Certaines images n'ont pas pu être importées (protection Google/CORS). Utilisez le bouton \"Ajouter image\" pour uploader votre logo manuellement.",
          { duration: 8000 }
        );
      }
    }
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader2 className="w-5 h-5 animate-spin text-zinc-400" />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="space-y-3"
      data-testid="signature-section"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <PenLine className="w-4 h-4 text-primary" />
          <h3 className="text-sm font-bold tracking-wide uppercase text-zinc-500">
            Signature
          </h3>
        </div>
        {lastSaved && (
          <Badge variant="outline" className="text-xs gap-1 text-emerald-600 border-emerald-200">
            <CheckCircle2 className="w-3 h-3" />
            Sauvegardée
          </Badge>
        )}
      </div>

      <div
        className="border rounded-xl overflow-hidden bg-white border-zinc-200 focus-within:ring-2 focus-within:ring-primary/30 focus-within:border-primary transition-all"
        data-testid="signature-editor-wrapper"
      >
        {/* Mini toolbar */}
        <div className="flex items-center gap-1 px-3 py-2 border-b border-zinc-100 bg-zinc-50/70">
          <input
            ref={imageInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleImageUpload}
            data-testid="signature-image-input"
          />
          <Button
            variant="ghost"
            size="sm"
            className="h-7 gap-1.5 text-xs text-zinc-600 hover:text-primary"
            onClick={() => imageInputRef.current?.click()}
            data-testid="signature-add-image-button"
          >
            <ImagePlus className="w-3.5 h-3.5" />
            Ajouter image
          </Button>
          {html && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 gap-1.5 text-xs text-zinc-400 hover:text-red-500"
              onClick={handleClear}
              data-testid="signature-clear-button"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Effacer
            </Button>
          )}
        </div>

        {/* Editor */}
        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          className="min-h-[100px] max-h-[200px] overflow-y-auto w-full p-4 focus:outline-none text-sm leading-relaxed text-zinc-800 [&_img]:max-w-[200px] [&_img]:h-auto"
          onInput={handleInput}
          onPaste={handlePaste}
          data-placeholder="Collez ou créez votre signature ici (avec image, logo...)"
          data-testid="signature-editor-content"
          style={{ wordBreak: "break-word" }}
        />
      </div>

      <p className="text-xs text-zinc-400">
        Astuce : collez directement votre signature depuis Gmail/Outlook, ou uploadez votre logo via le bouton "Ajouter image".
      </p>

      <Button
        className="gap-2 rounded-full w-full"
        onClick={handleSave}
        disabled={saving}
        data-testid="signature-save-button"
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
        Sauvegarder la signature
      </Button>
    </motion.div>
  );
}
